class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
    this.onProvisionalRowListener = this.onProvisionalRow.bind(this);
    this.onProductAddedListener = this.onProductAdded.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
    document.addEventListener('cart:provisional-row', this.onProvisionalRowListener);
    document.addEventListener('product-ajax:added', this.onProductAddedListener);
    this.initScrollFades();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
    document.removeEventListener('cart:provisional-row', this.onProvisionalRowListener);
    document.removeEventListener('product-ajax:added', this.onProductAddedListener);
  }

  onProductAdded(event) {
    const product = event && event.detail && event.detail.product;
    const title = product && (product.product_title || product.title);
    if (!title) return;
    const statusEl = this.querySelector('[data-cart-drawer-status]');
    if (statusEl) {
      // Polite live region — read after the SR finishes its current speech.
      statusEl.textContent = `Article ajouté au panier : ${title}`;
    }
  }

  onProvisionalRow(event) {
    const item = event && event.detail && event.detail.item;
    if (!item || !item.variant_id) return;

    const sectionId = FoxTheme.utils.getSectionId(this);
    const cartDrawerBody = this.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = this.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = this.querySelector(`#CartDrawerEmpty-${sectionId}`);
    const itemsList = cartDrawerBody && cartDrawerBody.querySelector('.cart-drawer__items-list');
    if (!itemsList) return;

    // Reveal the body when the cart was empty before the add — otherwise the
    // provisional row would be inserted inside a `hidden` wrapper.
    if (cartDrawerBody) cartDrawerBody.classList.remove('hidden');
    if (cartDrawerFooter) cartDrawerFooter.classList.remove('hidden');
    if (cartDrawerEmpty) cartDrawerEmpty.classList.add('hidden');

    // If the variant is somehow already shown (race with isIncrement detection),
    // the existing row is authoritative — don't duplicate it.
    if (itemsList.querySelector(`.cart-item[data-variant-id="${item.variant_id}"]`)) return;

    const esc = (str) => String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const formatMoney = (cents) => {
      if (FoxTheme.Currency && FoxTheme.Currency.formatMoney && FoxTheme.settings && FoxTheme.settings.moneyFormat) {
        return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    };

    const linePrice = typeof item.final_line_price === 'number'
      ? item.final_line_price
      : (item.final_price || item.price || 0) * (item.quantity || 1);

    let imageUrl = (item.featured_image && item.featured_image.url) || item.image || '';
    if (imageUrl && imageUrl.indexOf('width=') === -1) {
      imageUrl += (imageUrl.indexOf('?') === -1 ? '?' : '&') + 'width=200';
    }

    const productTitle = item.product_title || item.product_title_safe || item.title || '';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : '';
    const url = item.url || '#';

    const li = document.createElement('li');
    li.className = 'cart-item cart-item--pending cart-item--new';
    li.setAttribute('data-pending', 'true');
    li.setAttribute('data-variant-id', String(item.variant_id));
    li.setAttribute('data-quantity', String(item.quantity || 1));

    li.innerHTML = `
      <div class="cart-item__product">
        ${imageUrl ? `
          <a class="cart-item__media blocks-radius media-wrapper" href="${esc(url)}" tabindex="-1" aria-label="${esc(productTitle)}">
            <img src="${esc(imageUrl)}" alt="" loading="eager" width="100" height="100">
          </a>` : ''}
        <div class="cart-item__details">
          <div class="cart-item__info">
            <div class="cart-item__info-top">
              <span class="cart-item__title-wrapper">
                <a href="${esc(url)}" class="cart-item__title text-pcard-title cart-item__title-link reversed-link">
                  <span class="cart-item__title-text">${esc(productTitle)}</span>
                </a>
              </span>
              ${variantTitle ? `
                <div class="cart-item__options">
                  <span class="cart-item__option-value text-sm text-subtext">${esc(variantTitle)}</span>
                </div>` : ''}
            </div>
            <div class="cart-item__actions-row">
              <div class="flex items-center gap-2">
                <div class="cart-quantity quantity">
                  <button type="button" name="minus" class="quantity__button" disabled aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                  <input class="quantity__input" type="number" value="${esc(item.quantity || 1)}" disabled readonly inputmode="numeric" size="2" aria-hidden="true">
                  <button type="button" name="plus" class="quantity__button" disabled aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 2.1875V11.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
              </div>
              <span class="cart-item__remove-trash" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </span>
            </div>
          </div>
          <div class="cart-item__price-column">
            <span class="cart-item__regular-price">${formatMoney(linePrice)}</span>
          </div>
        </div>
      </div>
    `;

    itemsList.insertBefore(li, itemsList.firstChild);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    // Optimistic defaults BEFORE animation starts — avoids pop-in glitch
    const cartItems = this.querySelector('cart-items');
    if (cartItems) {
      const itemCount = cartItems.querySelectorAll('.cart-item').length;
      // If 4+ items, overflow is virtually guaranteed → show gradient+badge immediately
      if (itemCount >= 4) {
        this.classList.add('cart-drawer--items-can-scroll-down');
      }
    }

    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }

    // Set up scroll listener + badge click (idempotent)
    this.initScrollFades();

    // After the drawer open transition fully completes, compute accurate scroll state
    this.addEventListener('drawer:handleAfterShow', () => {
      this.initScrollFades();
      this.querySelectorAll('cart-drawer-products-recommendation').forEach(reco => {
        if (typeof reco.refreshNavState === 'function') reco.refreshNavState();
      });
    }, { once: true });
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    // Snapshot existing variant IDs + quantities before update for bump animation
    const cartBody = cartElement.querySelector(`[id^="CartDrawerBody"]`);
    const oldVariants = new Map();
    if (cartBody) {
      cartBody.querySelectorAll('.cart-item[data-variant-id]').forEach(el => {
        const vid = el.getAttribute('data-variant-id');
        const qty = parseInt(el.getAttribute('data-quantity') || '0', 10);
        oldVariants.set(vid, (oldVariants.get(vid) || 0) + qty);
      });
    }

    // Detach recommendations from the DOM BEFORE innerHTML wipe to preserve the element
    const recoFooter = cartElement.querySelector('.cart-drawer__reco-footer');
    if (recoFooter) recoFooter.remove();

    try {
      // Section Rendering API (`?sections=`) returns JSON with the section's
      // innerHTML — lighter than `?section_id=` which returns full layout
      // shell, and matches the convention already used by the optimistic
      // add-to-cart flow in theme.js. cache: 'no-store' + cache-buster so a
      // previous identical request doesn't get served from browser cache
      // with stale data after a cart change.
      const response = await fetch(
        `${FoxTheme.routes.root_url}?sections=${this.sectionId}&_=${Date.now()}`,
        { cache: 'no-store' }
      );
      const json = await response.json();
      const sectionHTML = json && json[this.sectionId];
      if (!sectionHTML) return;

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(sectionHTML, 'text/html');

      const newCartEl = parsedHTML.getElementById(cartId);
      if (!newCartEl) return;

      // Only remove new reco if we have an existing one to preserve
      const newReco = newCartEl.querySelector('.cart-drawer__reco-footer');
      if (recoFooter && newReco) newReco.remove();

      // Replace content
      cartElement.innerHTML = newCartEl.innerHTML;

      // Tag new or increased-quantity items with bump animation
      const newCartBody = cartElement.querySelector(`[id^="CartDrawerBody"]`);
      if (newCartBody) {
        newCartBody.querySelectorAll('.cart-item[data-variant-id]').forEach(el => {
          const vid = el.getAttribute('data-variant-id');
          const qty = parseInt(el.getAttribute('data-quantity') || '0', 10);
          const oldQty = oldVariants.get(vid) || 0;
          if (qty > oldQty) {
            el.classList.add('cart-item--new');
            el.addEventListener('animationend', () => el.classList.remove('cart-item--new'), { once: true });
          }
        });
      }

      // Re-insert the preserved reco element (if we had one)
      if (recoFooter) {
        const footer = cartElement.querySelector('.cart-drawer__footer');
        if (footer) {
          recoFooter.style.display = '';
          const recoEl = recoFooter.querySelector('cart-drawer-products-recommendation');
          if (recoEl) {
            recoEl.classList.remove('hidden');
            recoEl.style.display = '';
          }
          footer.prepend(recoFooter);
          if (recoEl && typeof recoEl.filterCartProducts === 'function') recoEl.filterCartProducts();
        }
      }
      // If no existing reco, the new one from server is already in innerHTML and will initialize natively

      this.initScrollFades();

      if (event.detail && event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  initScrollFades() {
    const cartItems = this.querySelector('cart-items');
    if (!cartItems) return;
    const drawer = this;
    const update = () => {
      const scrollTop = cartItems.scrollTop;
      const scrollMax = cartItems.scrollHeight - cartItems.clientHeight;
      const hasOverflow = scrollMax > 5;
      const atTop = scrollTop <= 5;
      const atBottom = scrollMax - scrollTop <= 5;
      drawer.classList.toggle('cart-drawer--items-can-scroll-up', hasOverflow && !atTop);
      drawer.classList.toggle('cart-drawer--items-can-scroll-down', hasOverflow && !atBottom);
    };
    cartItems.removeEventListener('scroll', cartItems._scrollFadeHandler);
    cartItems._scrollFadeHandler = update;
    cartItems.addEventListener('scroll', update, { passive: true });

    const badge = this.querySelector('.cart-drawer__see-more-badge');
    if (badge) {
      badge.onclick = () => {
        // On mobile, scroll by roughly one item height; on desktop, 60% of viewport
        const isMobile = window.innerWidth < 640;
        const scrollAmount = isMobile ? 100 : cartItems.clientHeight * 0.6;
        cartItems.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      };
    }

    // Run after layout settles
    requestAnimationFrame(() => requestAnimationFrame(update));
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartAddonModal extends ModalComponent {
  constructor() {
    super();
  }

  /**
   * To avoid lost focus input elements
   */
  get focusElement() {
    return this.querySelector('input, textarea, select') || this.querySelector('button');
  }
}
customElements.define('cart-addon-modal', CartAddonModal);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // Per-line debounce timers
    this.debounceTimers = new Map();
    // Per-line pending request controllers
    this.pendingRequests = new Map();

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.closest('quantity-input')) {
        this.debouncedOnChange(event);
      }
    });
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantities = this.querySelectorAll('.cart-item__quantity');


    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantities, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantities, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    // Use addEventListener (was: mqlTablet.onchange =) so that multiple
    // <cart-items> instances on the page (e.g. cart drawer + main cart) don't
    // overwrite each other's handler. The handler is removed in
    // disconnectedCallback.
    this._mqlTablet = mqlTablet;
    this._mqlTabletHandler = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };
    mqlTablet.addEventListener('change', this._mqlTabletHandler);

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    // Clear per-line debounce timers — stop pending qty change handlers from
    // firing on a detached instance.
    if (this.debounceTimers) {
      this.debounceTimers.forEach((t) => clearTimeout(t));
      this.debounceTimers.clear();
    }
    // Abort any in-flight quantity update requests.
    if (this.pendingRequests) {
      this.pendingRequests.forEach((c) => { try { c.abort(); } catch (e) {} });
      this.pendingRequests.clear();
    }
    // Stop the remove batch timer (queued line removals).
    if (this._removeBatchTimer) {
      clearTimeout(this._removeBatchTimer);
      this._removeBatchTimer = null;
    }
    // Drop the matchMedia listener.
    if (this._mqlTablet && this._mqlTabletHandler) {
      this._mqlTablet.removeEventListener('change', this._mqlTabletHandler);
    }
  }

  debouncedOnChange(event) {
    const target = event.target;
    const line = target.dataset.index;
    const quantityInput = target.closest('quantity-input');
    
    // Cooldown from data attribute (default: 400ms)
    const cooldown = quantityInput?.dataset.cooldown
      ? parseInt(quantityInput.dataset.cooldown, 10)
      : 400;

    // Cancel previous timer for this line
    if (this.debounceTimers.has(line)) {
      clearTimeout(this.debounceTimers.get(line));
    }

    // Show loader immediately for visual feedback
    this.showLoader(line);

    // Create new debounce timer
    const timer = setTimeout(() => {
      this.debounceTimers.delete(line);
      this.onChange(target);
    }, cooldown);

    this.debounceTimers.set(line, timer);
  }

  onChange(target) {
    this.updateQuantity(
      target.dataset.index,
      target.value,
      document.activeElement.getAttribute('name'),
      target
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    } else {
      // Instead of redirecting to /cart, refresh the cart drawer in place
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    }
    console.error(errors);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    const sectionHtml = event.cart.sections && event.cart.sections[sectionId];

    // sectionHtml may be missing on the optimistic-UI fast path emitted by
    // theme.js for increments: the drawer is updated as soon as /cart.js is
    // back, then a background fetch dispatches another cartUpdate with the
    // section HTML once it arrives. In that case we go through the
    // incremental-patch branch below using only event.cart, and skip the
    // footer update — the second cartUpdate will reconcile it.
    const sectionToRender = sectionHtml ? new DOMParser().parseFromString(sectionHtml, 'text/html') : null;

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      // Snapshot existing lines + quantities before update, keyed by cart line
      // key (variant + properties hash) rather than variant id. The theme
      // deliberately produces several lines for the same variant through hidden
      // properties — `_recommendation` (cart-drawer.liquid) and `_cross-selling`
      // (theme.js) — so a variant-keyed map conflates them: quantities get
      // summed into a total that matches no row, and every lookup below resolves
      // to the first matching row, patching it with another line's state while
      // leaving the other one stale. The line key is unique and stable across
      // quantity changes and sibling removals.
      const oldLines = new Map();
      if (cartDrawerBody) {
        cartDrawerBody.querySelectorAll('.cart-item[data-key]').forEach(el => {
          const key = el.getAttribute('data-key');
          const qty = parseInt(el.getAttribute('data-quantity') || '0', 10);
          oldLines.set(key, qty);
        });
      }

      const cartDrawerBodyUpdate = sectionToRender ? sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`) : null;
      const cartDrawerFooterUpdate = sectionToRender ? sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`) : null;
      const cartDrawerEmptyUpdate = sectionToRender ? sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`) : null;

      // === Try incremental patch on the body ===
      // The body innerHTML wipe + reconstruct is the dominant O(N items)
      // cost. When the new cart only differs from the current DOM by quantity
      // changes on already-displayed items, we can patch the data attribute
      // and the quantity input in place. The footer is still re-rendered
      // below because totals + free shipping bar may have changed and the
      // footer is small (constant cost).
      let didIncrementalBodyPatch = false;
      if (cartDrawerBody && oldLines.size > 0 && Array.isArray(event.cart.items)) {
        const newKeys = new Set();
        let canPatch = true;
        for (const item of event.cart.items) {
          const key = String(item.key);
          if (!oldLines.has(key)) { canPatch = false; break; }
          newKeys.add(key);
        }
        if (canPatch) {
          for (const key of oldLines.keys()) {
            if (!newKeys.has(key)) { canPatch = false; break; }
          }
        }
        // Bail out of the incremental path if anything is queued for removal
        // — that flow needs the full re-render to clean up ghost rows.
        if (canPatch && this._pendingRemoveKeys && this._pendingRemoveKeys.size > 0) {
          canPatch = false;
        }
        // Bail out if a provisional row is in the body (optimistic-UI insert
        // from theme.js for new variants). The placeholder lacks the full
        // Liquid markup (no quantity-input controls, no remove button, no
        // upsell etc.) — a full re-render is required to replace it cleanly.
        if (canPatch && cartDrawerBody.querySelector('.cart-item[data-pending]')) {
          canPatch = false;
        }
        if (canPatch) {
          for (const item of event.cart.items) {
            const key = String(item.key);
            const el = cartDrawerBody.querySelector(`.cart-item[data-key="${key}"]`);
            if (!el) continue;
            const oldQty = oldLines.get(key) || 0;
            if (item.quantity !== oldQty) {
              el.setAttribute('data-quantity', item.quantity);
              const qtyInput = el.querySelector('input.quantity__input');
              if (qtyInput) qtyInput.value = item.quantity;
              if (item.quantity > oldQty) {
                el.classList.add('cart-item--new');
                el.addEventListener('animationend', () => el.classList.remove('cart-item--new'), { once: true });
              }
            }
            // Reconcile the line's price zone from the authoritative server
            // HTML. Native quantity-tier discounts (driven by pavo) toggle a
            // line's struck-through / sale price / savings badge the moment its
            // quantity crosses a threshold — up or down — state the client-side
            // qty patch above cannot compute. When the mutation response carried
            // the section HTML (the drawer always bundles it via
            // cart:grouped-sections), copy the server-rendered price column so
            // the line reflects the real discount instead of a stale one. Only
            // .cart-item__price-column carries that state in the drawer markup;
            // the footer totals/discount summary reconcile separately below.
            // Diff-guarded to avoid needless DOM churn on unchanged lines.
            if (sectionToRender) {
              const serverEl = sectionToRender.querySelector(`.cart-item[data-key="${key}"]`);
              const currentPriceEl = el.querySelector('.cart-item__price-column');
              const serverPriceEl = serverEl && serverEl.querySelector('.cart-item__price-column');
              if (currentPriceEl && serverPriceEl && currentPriceEl.innerHTML !== serverPriceEl.innerHTML) {
                currentPriceEl.innerHTML = serverPriceEl.innerHTML;
              }
            }
          }
          didIncrementalBodyPatch = true;
        }
      }

      // Patch the checkout-button price client-side on the optimistic-UI
      // fast path (no section HTML available). Without this, the button
      // would still display the old total until the background sections
      // fetch lands ~500ms later — that gap is the main reason the flow
      // feels slow on a large cart even with the incremental body patch.
      if (didIncrementalBodyPatch && !sectionToRender && cartDrawerFooter) {
        const fmt = FoxTheme.settings && FoxTheme.settings.moneyFormat;
        if (fmt && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
          const priceEl = cartDrawerFooter.querySelector('.cart-drawer__checkout-price');
          if (priceEl && typeof event.cart.total_price === 'number') {
            priceEl.innerHTML = FoxTheme.Currency.formatMoney(event.cart.total_price, fmt);
          }
        }
      }

      // <cart-order-progress> and <cart-shipping-display> are supposed to
      // self-update via the cart:updated event dispatched at the end of this
      // method. On the optimistic fast path that proved unreliable: the
      // checkout price (patched in-place above) refreshed while the progress
      // banner stayed frozen on the previous total — a visible inconsistency
      // (e.g. "Ajoutez 1,05€" next to a "Commander 1,05€" button). Drive the
      // *visible* elements directly from the authoritative event.cart, same
      // as the price, so they can never lag behind it.
      if (didIncrementalBodyPatch && !sectionToRender && cartDrawer && typeof event.cart.total_price === 'number') {
        const progressEl = cartDrawer.querySelector('cart-order-progress');
        if (progressEl) {
          progressEl.cartTotal = event.cart.total_price;
          if (typeof progressEl.update === 'function') progressEl.update();
          if (typeof progressEl.updateCheckoutButton === 'function') progressEl.updateCheckoutButton();
        }
        const shippingEl = cartDrawer.querySelector('cart-shipping-display');
        if (shippingEl) {
          shippingEl.cartTotal = event.cart.total_price;
          if (typeof shippingEl.calculateShipping === 'function') shippingEl.calculateShipping();
        }
      }

      // Safety net: when there's no section HTML to fall back to AND the
      // incremental patch couldn't run (e.g. variant truly new but the
      // theme.js detector misfired), request a full refresh so the drawer
      // doesn't end up out of sync with the server. Skip when an optimistic
      // provisional row is in flight — the background ?sections= fetch from
      // theme.js is on its way and will reconcile the body in a moment.
      if (!didIncrementalBodyPatch && !cartDrawerBodyUpdate) {
        if (cartDrawerBody && cartDrawerBody.querySelector('.cart-item[data-pending]')) {
          return;
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        return;
      }

      if (!didIncrementalBodyPatch && cartDrawerBodyUpdate) {
        // Re-hide items still queued for removal BEFORE inserting into DOM (prevents ghost flash)
        if (this._pendingRemoveKeys && this._pendingRemoveKeys.size > 0) {
          cartDrawerBodyUpdate.querySelectorAll('.cart-item').forEach(el => {
            const removeBtn = el.querySelector('[data-line-key]');
            if (removeBtn && this._pendingRemoveKeys.has(removeBtn.dataset.lineKey)) {
              el.classList.add('cart-item--removing');
            }
          });
        }

        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;

        // Tag new or increased-quantity items with bump animation
        cartDrawerBody.querySelectorAll('.cart-item[data-key]').forEach(el => {
          const key = el.getAttribute('data-key');
          const qty = parseInt(el.getAttribute('data-quantity') || '0', 10);
          const oldQty = oldLines.get(key) || 0;
          if (qty > oldQty) {
            el.classList.add('cart-item--new');
            el.addEventListener('animationend', () => el.classList.remove('cart-item--new'), { once: true });
          }
        });
      }
      if (cartDrawerFooterUpdate) {
        // Detach existing recos to preserve the initialized custom element
        const recoBlock = cartDrawerFooter.querySelector('.cart-drawer__reco-footer');
        if (recoBlock) recoBlock.remove();
        // Only remove new reco from parsed HTML if we have an existing one to preserve
        const newReco = cartDrawerFooterUpdate.querySelector('.cart-drawer__reco-footer');
        if (recoBlock && newReco) newReco.remove();
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
        // Re-insert existing reco (preserved element)
        if (recoBlock) {
          recoBlock.style.display = '';
          const recoEl = recoBlock.querySelector('cart-drawer-products-recommendation');
          if (recoEl) {
            recoEl.classList.remove('hidden');
            recoEl.style.display = '';
          }
          cartDrawerFooter.prepend(recoBlock);
          if (recoEl && typeof recoEl.filterCartProducts === 'function') recoEl.filterCartProducts(event.cart);
        }
        // If no existing reco, new one from server is already in innerHTML
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');

        // Re-init empty state reco nav after layout settles
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const emptyReco = cartDrawerEmpty.querySelector('.cart-drawer-reco-empty');
          if (!emptyReco) return;
          const slider = emptyReco.querySelector('.cart-drawer-products-recommendation__slider');
          const prevBtn = emptyReco.querySelector('.cart-drawer-products-recommendation__nav-btn--prev');
          const nextBtn = emptyReco.querySelector('.cart-drawer-products-recommendation__nav-btn--next');
          if (!slider) return;

          const refreshNav = () => {
            const canScroll = slider.scrollWidth > slider.clientWidth + 1;
            const isAtStart = slider.scrollLeft <= 1;
            const isAtEnd = slider.scrollLeft >= slider.scrollWidth - slider.clientWidth - 1;
            if (prevBtn) prevBtn.style.opacity = (!canScroll || isAtStart) ? '0.3' : '1';
            if (nextBtn) nextBtn.style.opacity = (!canScroll || isAtEnd) ? '0.3' : '1';
          };

          // Bind click handlers if not already bound
          if (!emptyReco._navBound) {
            emptyReco._navBound = true;
            const getItemWidth = () => {
              const item = slider.querySelector('.cart-drawer-products-recommendation__item');
              const gap = parseFloat(getComputedStyle(slider).gap) || 20;
              return item ? item.offsetWidth + gap : 350;
            };
            if (prevBtn) prevBtn.addEventListener('click', () => slider.scrollBy({ left: -getItemWidth(), behavior: 'smooth' }));
            if (nextBtn) nextBtn.addEventListener('click', () => slider.scrollBy({ left: getItemWidth(), behavior: 'smooth' }));
            slider.addEventListener('scroll', refreshNav, { passive: true });
          }
          refreshNav();
        }));
      }

      // Re-init scroll fades on the cart-drawer after body innerHTML replacement
      const drawerEl = cartDrawer.closest('cart-drawer');
      if (drawerEl && typeof drawerEl.initScrollFades === 'function') drawerEl.initScrollFades();
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
        if (event.cart.item_count > 0) {
          mainCart.closest('.cart').classList.remove('is-empty');
        } else {
          mainCart.closest('.cart').classList.add('is-empty');
        }
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    // Abort previous request for this line if pending
    if (this.pendingRequests.has(line)) {
      this.pendingRequests.get(line).abort();
    }

    // Create AbortController for this request
    const abortController = new AbortController();
    this.pendingRequests.set(line, abortController);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToBundle,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body },
      signal: abortController.signal
    })
      .then((response) => response.json())
      .then((parsedState) => {
        this.pendingRequests.delete(line);
        this.hideLoader(line);
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState, target, line, name });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.pendingRequests.delete(line);
        console.error(error);
      });
  }

  // Batched remove: collect clicks, send ONE /cart/update.js request
  _pendingRemoveKeys = new Set();
  _removeBatchTimer = null;
  _removeInFlight = false;

  queueRemoveByKey(key, line) {
    if (line) this.showLoader(line);
    this._pendingRemoveKeys.add(key);

    // Debounce: collect all rapid clicks, then send one batch request
    clearTimeout(this._removeBatchTimer);
    if (!this._removeInFlight) {
      this._removeBatchTimer = setTimeout(() => this._flushRemoves(), 100);
    }
  }

  async _flushRemoves() {
    if (this._pendingRemoveKeys.size === 0 || this._removeInFlight) return;
    this._removeInFlight = true;

    const keys = [...this._pendingRemoveKeys];
    const updates = {};
    keys.forEach(key => { updates[key] = 0; });

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    try {
      const response = await fetch(`${FoxTheme.routes.cart_update_url}`, {
        ...FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({ updates, sections: sectionsToBundle })
      });
      const parsedState = await response.json();

      keys.forEach(k => this._pendingRemoveKeys.delete(k));
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
    } catch (error) {
      keys.forEach(k => this._pendingRemoveKeys.delete(k));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      console.error(error);
    }

    this._removeInFlight = false;

    // If new removes came in while the request was in flight, flush again
    if (this._pendingRemoveKeys.size > 0) {
      this._flushRemoves();
    }
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove('btn--loading');
      });
    }
  }

}
customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItem = this.closest('.cart-item');
      // Prevent double-click on same item
      if (cartItem && cartItem.classList.contains('cart-item--removing')) return;
      // Mark as removing immediately — hides visually, prevents further clicks
      if (cartItem) cartItem.classList.add('cart-item--removing');

      const cartItems = this.closest('cart-items');
      // Use serialized queue with line item key (stable ID) to avoid
      // "line parameter is invalid" errors when multiple items are removed quickly
      const lineKey = this.dataset.lineKey;
      if (lineKey) {
        cartItems.queueRemoveByKey(lineKey, this.dataset.index);
      } else {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
      .catch(console.error);
  }
}
customElements.define('cart-note', CartNote);

class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  get submitEl() {
    return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl = this._messageEl || this.querySelector('.form__message'));
  }

  get couponEl() {
    return (this._couponEl = this._couponEl || this.querySelector('input[name="discount"]'));
  }

  get cartAddonDrawer() {
    return this.closest('.cart-addons-drawer');
  }

  get cartDiscountsEl() {
    return document.querySelector('.cart__discounts');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.discount');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl.value;
    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', discounts.join(','));

    config.body = formData;

    this.submitEl.setAttribute('aria-disabled', 'true');
    this.submitEl.classList.add('btn--loading');

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === newDiscountCode && discount.applicable === false;
          })
        ) {
          this.couponEl.value = '';
          this.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.hide();
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle('!hidden', !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
customElements.define('cart-discount', CartDiscount, { extends: 'form' });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: '.cart__discounts',
      item: '.discount',
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute('aria-disabled') === 'true') return;

    this.setAttribute('aria-disabled', 'true');
    this.classList.add('btn--loading');

    this.discounts = [];

    const thisItem = this.closest('li');
    const items = this.listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item != thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', this.discounts.join(','));

    config.body = formData;

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.removeAttribute('aria-disabled');
        this.classList.remove('btn--loading');
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };
}
customElements.define('cart-discount-remove', CartDiscountRemove, { extends: 'button' });

class CalculateShipping extends CartAddonModal {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartShippingDisplay extends HTMLElement {
  constructor() {
    super();
    // Read configuration from data attributes
    this.freeThreshold = parseInt(this.dataset.freeThreshold, 10) || 0;
    this.freeText = this.dataset.freeText || 'OFFERTS';
    this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;

    // Parse shipping tier thresholds
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
      this.tiers.sort((a, b) => a.max - b.max);
    } catch (e) {
      console.error('Error parsing shipping tiers:', e);
      this.tiers = [];
    }

    this.onCartUpdate = this.calculateShipping.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:updated', this.onCartUpdate);
    this.calculateShipping();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdate);
  }

  calculateShipping(event) {
    const textEl = this.querySelector('.cart-shipping-display__text');
    if (!textEl) return;

    // Update cart total from event if available
    if (event && event.detail && event.detail.cart) {
      this.cartTotal = event.detail.cart.total_price || 0;
    }

    // Free shipping if above threshold
    if (this.freeThreshold > 0 && this.cartTotal >= this.freeThreshold) {
      textEl.textContent = this.freeText;
      return;
    }

    // Find matching shipping tier (sorted by max ascending)
    let rate = null;
    for (const tier of this.tiers) {
      if (this.cartTotal < tier.max) {
        rate = tier.rate;
        break;
      }
    }

    // Fallback to last tier if no match found
    if (rate === null && this.tiers.length > 0) {
      rate = this.tiers[this.tiers.length - 1].rate;
    }

    if (rate !== null && rate > 0) {
      textEl.textContent = this.formatMoney(rate);
    } else {
      // rate === 0 or rate === null → display FREE
      textEl.textContent = this.freeText;
    }
  }

  formatMoney(cents) {
    // Use theme's money formatter if available
    if (FoxTheme.Currency && FoxTheme.Currency.formatMoney && FoxTheme.settings && FoxTheme.settings.moneyFormat) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }
    
    // Simple fallback
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${amount} €`;
  }

}

customElements.define('cart-shipping-display', CartShippingDisplay);

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    this.carousel = false;
    this.isLoading = false;

    this.classes = {
      grid: 'f-grid',
      swiper: 'swiper',
      swiperWrapper: 'swiper-wrapper',
      loading: 'is-loading',
    };

    // Handle Safari separately
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    if (isSafari) {
      this.init();
    } else {
      FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  disconnectedCallback() {
    // Skip destroyCarousel for custom design — the element is detached/re-inserted
    // during cart updates; destroyCarousel would add 'hidden' class or 'f-grid' class
    if (!this.classList.contains('cart-drawer-products-recommendation--custom')) {
      this.destroyCarousel();
    }
    if (this._handleCarouselBound) {
      const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
      mql.removeEventListener('change', this._handleCarouselBound);
    }
  }

  init() {
    if (this.isLoading || !this.dataset.url) return;

    this.isLoading = true;
    this.classList.add(this.classes.loading);

    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (!sectionInnerHTML) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;

          // Client-side filter: hide reco cards for products already in cart
          this.filterCartProducts();

          // Check if custom design is used
          if (this.classList.contains('cart-drawer-products-recommendation--custom')) {
            this.initCustomNavigation();
          } else {
            this._handleCarouselBound = this.handleCarousel.bind(this);
            const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
            mql.addEventListener('change', this._handleCarouselBound);
            this.handleCarousel();
          }

          this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
        } else {
          this.closest('.shopify-section')?.remove();
        }
      })
      .catch((error) => {
        console.error('Error loading recommendations:', error);
        this.classList.add('has-error');
      })
      .finally(() => {
        this.isLoading = false;
        this.classList.remove(this.classes.loading);
      });
  }

  initCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }

    if (typeof this.carousel !== 'object') {
      try {
        this.container.classList.add(this.classes.swiper);
        this.wrapper.classList.remove(this.classes.grid);
        this.wrapper.classList.add(this.classes.swiperWrapper);

        let options = {
          slidesPerView: this.columns,
          spaceBetween: 8,
          loop: false,
          navigation: {
            nextEl: this.sliderNext,
            prevEl: this.sliderPrev,
          },
        };

        const paginationEl = this.querySelector('.swiper-pagination');
        if (paginationEl) {
          options = {
            ...options,
            pagination: {
              el: paginationEl,
              clickable: true,
              type: 'fraction',
            },
          };
        }

        this.carousel = new FoxTheme.Carousel(this.slideContainer, options);

        if (this.carousel) {
          this.carousel.init();
          this.calcNavButtonsPosition();
        }
      } catch (error) {
        console.error('Error initializing carousel:', error);
        this.destroyCarousel();
      }
    }
  }

  handleCarousel() {
    const containerEl = this.container;
    this.columns = containerEl.dataset.columns;
    this.enableSliderMobile = containerEl.dataset.enableSliderMobile === 'true';

    if (FoxTheme.config.mqlMobile) {
      this.enableSliderMobile ? this.initCarousel() : this.destroyCarousel();
    } else {
      this.initCarousel();
    }
  }

  destroyCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }
    this.container?.classList.remove(this.classes.swiper);
    this.wrapper?.classList.remove(this.classes.swiperWrapper);
    this.wrapper?.classList.add(this.classes.grid);

    if (typeof this.sliderInstance !== 'object') return;
    this.carousel.slider.destroy();
    this.carousel = false;
  }

  calcNavButtonsPosition() {
    if (this.dataset.calcButtonPosition !== 'true') return;
    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get container() {
    return this.querySelector('.cart-drawer-products-recommendation__container') || this.querySelector('.cart-drawer-products-recommendation__container-custom');
  }

  get wrapper() {
    return this.querySelector('.cart-drawer-products-recommendation__wrapper') || this.querySelector('.cart-drawer-products-recommendation__slider');
  }

  async filterCartProducts(cartData) {
    // Server-side Liquid handles initial render (no flash on reload).
    // This JS method handles dynamic updates (product added while drawer is open).
    try {
      const cart = cartData || await (await fetch('/cart.js')).json();

      const cartHandles = new Set();
      cart.items.forEach(item => {
        if (item.handle) cartHandles.add(item.handle);
      });

      if (!cartHandles.size) return;

      const items = this.querySelectorAll('.cart-drawer-products-recommendation__item');
      let visibleCount = items.length;
      const toRemove = [];
      items.forEach(item => {
        const link = item.querySelector('a[href]');
        if (!link) return;
        const href = link.getAttribute('href');
        const match = href.match(/\/products\/([^/?#]+)/);
        if (match && cartHandles.has(match[1])) {
          visibleCount--;
          toRemove.push(item);
        }
      });

      if (!toRemove.length) return;

      // Phase 1: fade out + scale down (300ms)
      toRemove.forEach(item => item.classList.add('cart-drawer-products-recommendation__item--removing'));

      // Phase 2: collapse width so next cards slide in (starts at 300ms, lasts 350ms)
      setTimeout(() => {
        toRemove.forEach(item => item.classList.add('cart-drawer-products-recommendation__item--collapsing'));
      }, 300);

      // Phase 3: remove from DOM + update nav (at 700ms)
      setTimeout(() => {
        toRemove.forEach(item => { if (item.parentNode) item.remove(); });

        if (visibleCount <= 0) {
          const recoFooter = this.closest('.cart-drawer__reco-footer');
          if (recoFooter) {
            recoFooter.style.display = 'none';
          } else {
            this.style.display = 'none';
          }
        }

        this.updateNavVisibility();
        this.refreshNavState();
        this.dispatchEvent(new CustomEvent('recommendations:updated', { bubbles: true }));
      }, 700);
    } catch (e) {}
  }

  updateNavVisibility() {
    const navContainer = this.querySelector('.cart-drawer-products-recommendation__nav');
    if (!navContainer) return;
    const slider = this.querySelector('.cart-drawer-products-recommendation__slider');
    const visibleItems = slider ? slider.querySelectorAll('.cart-drawer-products-recommendation__item') : [];
    if (visibleItems.length <= 1) {
      navContainer.style.display = 'none';
    } else {
      navContainer.style.display = '';
    }
  }

  initCustomNavigation() {
    const prevBtn = this.querySelector('.cart-drawer-products-recommendation__nav-btn--prev');
    const nextBtn = this.querySelector('.cart-drawer-products-recommendation__nav-btn--next');
    const slider = this.querySelector('.cart-drawer-products-recommendation__slider');
    const dots = this.querySelectorAll('.cart-drawer-products-recommendation__dot');
    const items = slider ? slider.querySelectorAll('.cart-drawer-products-recommendation__item') : [];

    if (!slider) return;

    // Hide nav arrows if ≤1 item
    this.updateNavVisibility();

    // Only bind click listeners once
    if (!this._navInitialized) {
      this._navInitialized = true;

      const getItemWidth = () => {
        if (!items.length) return 350;
        return items[0].offsetWidth + 20; // item width + gap
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          slider.scrollBy({ left: -getItemWidth(), behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          slider.scrollBy({ left: getItemWidth(), behavior: 'smooth' });
        });
      }

      // Dot click → scroll to that item
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          if (items[i]) {
            items[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          }
        });
      });

      slider.addEventListener('scroll', () => this.refreshNavState(), { passive: true });
    }

    // Store references for refreshNavState
    this._navPrevBtn = prevBtn;
    this._navNextBtn = nextBtn;
    this._navSlider = slider;
    this._navDots = dots;

    // Set optimistic defaults immediately (no dimension check needed):
    // prev=disabled (scroll starts at 0), next=enabled (multiple items exist)
    if (items.length > 1) {
      if (prevBtn) { prevBtn.disabled = true; prevBtn.style.opacity = '0.3'; }
      if (nextBtn) { nextBtn.disabled = false; nextBtn.style.opacity = '1'; }
    }
  }

  refreshNavState() {
    const slider = this._navSlider;
    const prevBtn = this._navPrevBtn;
    const nextBtn = this._navNextBtn;
    const dots = this._navDots;
    if (!slider) return;

    const currentItems = slider.querySelectorAll('.cart-drawer-products-recommendation__item');
    const canScroll = slider.scrollWidth > slider.clientWidth + 1;
    const isAtStart = slider.scrollLeft <= 0;
    const isAtEnd = slider.scrollLeft >= slider.scrollWidth - slider.clientWidth - 1;

    if (prevBtn) {
      prevBtn.disabled = !canScroll || isAtStart;
      prevBtn.style.opacity = (!canScroll || isAtStart) ? '0.3' : '1';
    }
    if (nextBtn) {
      nextBtn.disabled = !canScroll || isAtEnd;
      nextBtn.style.opacity = (!canScroll || isAtEnd) ? '0.3' : '1';
    }

    if (dots && dots.length && currentItems.length) {
      const scrollLeft = slider.scrollLeft;
      let activeIndex = 0;
      let minDist = Infinity;
      currentItems.forEach((item, i) => {
        const dist = Math.abs(item.offsetLeft - slider.offsetLeft - scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          activeIndex = i;
        }
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('cart-drawer-products-recommendation__dot--active', i === activeIndex);
      });
    }
  }
}
customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

/* ── Cart page sticky checkout: visibility + price sync ── */
(function() {
  function syncStickyPrices() {
    var sticky = document.getElementById('cart-sticky-checkout');
    if (!sticky) return;
    var computed = document.getElementById('cart-page-computed-prices');
    if (!computed) return;

    var fmt = window.FoxTheme.settings.moneyFormat;
    var grandTotal = parseInt(computed.dataset.grandTotal || '0', 10);
    var compareWithShipping = parseInt(computed.dataset.compareWithShipping || '0', 10);
    var hasDiscount = computed.dataset.hasDiscount === 'true';

    var stickyCurrentEl = sticky.querySelector('.cart-page__sticky-checkout-current');
    var stickyCompareEl = sticky.querySelector('.cart-page__sticky-checkout-compare');
    var priceWrap = sticky.querySelector('.cart-page__sticky-checkout-price');

    if (stickyCurrentEl) {
      stickyCurrentEl.textContent = FoxTheme.Currency.formatMoney(grandTotal, fmt);
    }

    if (hasDiscount) {
      if (stickyCompareEl) {
        stickyCompareEl.textContent = FoxTheme.Currency.formatMoney(compareWithShipping, fmt);
        stickyCompareEl.style.display = '';
      } else if (priceWrap && stickyCurrentEl) {
        var el = document.createElement('span');
        el.className = 'cart-page__sticky-checkout-compare';
        el.textContent = FoxTheme.Currency.formatMoney(compareWithShipping, fmt);
        priceWrap.insertBefore(el, stickyCurrentEl);
      }
    } else if (stickyCompareEl) {
      stickyCompareEl.style.display = 'none';
    }
  }

  function syncMinOrder(cart) {
    var sticky = document.getElementById('cart-sticky-checkout');
    if (!sticky) return;
    var progressEl = document.querySelector('cart-order-progress');
    var minOrder = progressEl ? parseInt(progressEl.dataset.minimumOrder || '0', 10) : 0;
    var totalPrice = cart ? cart.total_price : parseInt(progressEl && progressEl.dataset.cartTotal || '0', 10);
    var minNotReached = minOrder > 0 && totalPrice < minOrder;

    sticky.classList.toggle('cart-page__sticky-checkout--min-not-reached', minNotReached);
    var stickyBtn = sticky.querySelector('.cart-page__sticky-checkout-btn');
    if (stickyBtn) {
      stickyBtn.classList.toggle('cart-page__sticky-checkout-btn--disabled', minNotReached);
      if (minNotReached) stickyBtn.setAttribute('aria-disabled', 'true');
      else stickyBtn.removeAttribute('aria-disabled');
    }
  }

  function checkStickyVisibility() {
    var sticky = document.getElementById('cart-sticky-checkout');
    if (!sticky) return;
    var btn = document.querySelector('.cart-page__checkout-btn');
    if (!btn) { sticky.classList.remove('cart-page__sticky-checkout--show'); return; }
    var rect = btn.getBoundingClientRect();
    sticky.classList.toggle('cart-page__sticky-checkout--show', rect.top >= window.innerHeight || rect.bottom <= 0);
  }

  // Scroll/resize
  window.addEventListener('scroll', checkStickyVisibility, { passive: true });
  window.addEventListener('resize', checkStickyVisibility, { passive: true });

  // Initial: check visibility + min order
  function init() {
    checkStickyVisibility();
    syncMinOrder(null);
  }
  if (document.readyState === 'complete') { init(); }
  else { window.addEventListener('load', init); }

  // Cart updates
  if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function(event) {
      syncMinOrder(event.cart);
      // Observe main-cart for innerHTML changes (section rendering)
      var mainCart = document.querySelector('main-cart');
      if (mainCart && !mainCart._priceSyncObserver) {
        mainCart._priceSyncObserver = new MutationObserver(function() {
          requestAnimationFrame(function() { syncStickyPrices(); checkStickyVisibility(); });
        });
        mainCart._priceSyncObserver.observe(mainCart, { childList: true, subtree: true });
      }
      // Fallback
      setTimeout(function() { syncStickyPrices(); checkStickyVisibility(); }, 500);
    });
  }
})();

/* ── Cart page recommendations: overflow gradient ── */
(function() {
  var boundSlider = null;

  function check() {
    var wrap = document.querySelector('.cart-page__recommendations');
    if (!wrap) return;
    var slider = wrap.querySelector('.cart-drawer-products-recommendation__slider');
    if (!slider) {
      wrap.setAttribute('data-overflow-left', 'false');
      wrap.setAttribute('data-overflow-right', 'false');
      return;
    }
    if (slider !== boundSlider) {
      boundSlider = slider;
      slider.addEventListener('scroll', check, { passive: true });
    }
    var sl = slider.scrollLeft;
    var sw = slider.scrollWidth;
    var cw = slider.clientWidth;
    var hasOverflow = sw > cw + 5;
    var atEnd = sw - sl - cw <= 5;
    wrap.setAttribute('data-overflow-left', hasOverflow && atEnd && sl > 5 ? 'true' : 'false');
    wrap.setAttribute('data-overflow-right', hasOverflow && !atEnd ? 'true' : 'false');
  }

  function resetAndCheck() {
    boundSlider = null;
    check();
    requestAnimationFrame(check);
  }

  // React to cart updates (section re-rendering)
  if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, resetAndCheck);
  }

  // React to recommendations loading/updating (events bubble from custom element)
  document.addEventListener('recommendations:loaded', resetAndCheck);
  document.addEventListener('recommendations:updated', resetAndCheck);

  window.addEventListener('resize', check, { passive: true });

  // Initial check
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resetAndCheck);
  } else {
    resetAndCheck();
  }
})();

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
    this.state = null;
  }

  connectedCallback() {
    this.updateShippingGoal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGoal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGoal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }

    this.dispatchState();
  }

  dispatchState() {
    if (this.state === this.goalDone) return;
    this.state = this.goalDone;
    document.dispatchEvent(
      new CustomEvent('free-shipping:state', {
        detail: {
          isFree: this.goalDone,
        },
      })
    );
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};

class CartOrderProgress extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.minOrder = parseInt(this.dataset.minimumOrder, 10) || 0;
    this.freeShipping = parseInt(this.dataset.freeShipping, 10) || 0;
    this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
    this.maxThreshold = Math.max(this.minOrder, this.freeShipping);

    this.update();
    this.updateCheckoutButton();

    this._onCartUpdated = this._onCartUpdated.bind(this);
    document.addEventListener('cart:updated', this._onCartUpdated);
  }

  disconnectedCallback() {
    if (this._onCartUpdated) {
      document.removeEventListener('cart:updated', this._onCartUpdated);
    }
  }

  _onCartUpdated(event) {
    if (event.detail && event.detail.cart) {
      this.cartTotal = event.detail.cart.total_price || 0;
      this.update();
      this.updateCheckoutButton();
    }
  }

  getState() {
    if (this.minOrder > 0 && this.cartTotal < this.minOrder) return 'min-not-reached';
    if (this.freeShipping > 0 && this.cartTotal < this.freeShipping) return 'min-reached';
    if (this.freeShipping > 0) return 'free';
    return 'complete';
  }

  update() {
    if (this.maxThreshold <= 0) return;

    const fills = this.querySelectorAll('.cart-order-progress__fill');
    const indicators = this.querySelectorAll('.cart-order-progress__indicator');

    let pct = (this.cartTotal / this.maxThreshold) * 100;
    if (pct > 100) pct = 100;

    fills.forEach(el => { el.style.width = pct + '%'; });
    indicators.forEach(el => { el.style.left = pct + '%'; });

    const state = this.getState();

    const banners = {
      'min-not-reached': this.querySelector('[data-state="min-not-reached"]'),
      'min-reached': this.querySelector('[data-state="min-reached"]'),
      'free': this.querySelector('[data-state="free"]'),
    };
    Object.entries(banners).forEach(([key, el]) => {
      if (el) el.hidden = (key !== state);
    });

    const dotMin = this.querySelector('[data-dot="min"]');
    const dotsEnd = this.querySelectorAll('[data-dot="end"]');
    if (dotMin) dotMin.hidden = state !== 'min-not-reached';
    dotsEnd.forEach(el => { el.hidden = (state === 'free' || state === 'complete'); });

    this.updateAmounts();
  }

  updateAmounts() {
    const format = FoxTheme.settings?.moneyFormat;
    if (!format || !FoxTheme.Currency?.formatMoney) return;

    if (this.minOrder > 0) {
      const leftMin = Math.max(0, this.minOrder - this.cartTotal);
      this.querySelectorAll('[data-left-to-min]').forEach(el => {
        el.innerHTML = FoxTheme.Currency.formatMoney(leftMin, format);
      });
    }

    if (this.freeShipping > 0) {
      const left = Math.max(0, this.freeShipping - this.cartTotal);
      this.querySelectorAll('[data-left-to-free]').forEach(el => {
        el.innerHTML = FoxTheme.Currency.formatMoney(left, format);
      });
    }
  }

  updateCheckoutButton() {
    const drawer = this.closest('cart-drawer');
    if (!drawer) return;
    const btn = drawer.querySelector('.cart-drawer__checkout-btn');
    if (!btn) return;

    const state = this.getState();
    if (state === 'min-not-reached') {
      btn.disabled = true;
      btn.classList.add('cart-drawer__checkout-btn--disabled');
    } else {
      btn.disabled = false;
      btn.classList.remove('cart-drawer__checkout-btn--disabled');
    }
  }
}
customElements.define('cart-order-progress', CartOrderProgress);
